import { Box, Typography } from '@mui/material';
import classNames from 'classnames';
import config from 'config/config';
import { useTournamentDetails } from 'modules/tournaments/hooks';
import moment from 'moment';
import React, { FC, useCallback } from 'react';
import Countdown, { CountdownRenderProps } from 'react-countdown';
import { IntlDateFormat } from 'shared/components/Format/IntlDateFormat';
import { usePadStart } from 'shared/hooks';
import { useStyles } from './TournamentCheckinTimer.styles';
import { useStyles as useEllipsisAnimationStyles } from 'shared/styles/EllipsisAnimation.styles';

interface TournamentCheckinTimerProps {
  date: string;
}

export const TournamentCheckinTimer: FC<TournamentCheckinTimerProps> = ({ date }) => {
  const { classes } = useStyles();
  const { classes: ellipsisAnimationClasses } = useEllipsisAnimationStyles();
  const { padStart } = usePadStart();

  const { isLiveTournament, isPendingTournament, isCheckinTournament } = useTournamentDetails();
  const updateTimeHandler = useCallback(
    (props: CountdownRenderProps) => {
      if (!props.total) {
        return null;
      }

      const { hours, days, minutes, seconds } = props;

      let content = '';
      if (minutes >= 1) {
        content = `${padStart(minutes)}m ${padStart(seconds)}s`;
      } else if (!minutes && seconds) {
        content = `${padStart(seconds)}s`;
      }

      if (hours === 0 && days === 0 && minutes < config.timeToTournamentCheckIn / 60) {
        return (
          <Box className={classes.fullDateContainer}>
            <Typography textAlign="center" className={classes.timer}>
              {content}
            </Typography>
          </Box>
        );
      }

      if (!isLiveTournament) {
        return (
          <Box className={classes.fullDateContainer}>
            <Typography textAlign="center" className={classes.fullDate}>
              <IntlDateFormat date={date} />
            </Typography>
          </Box>
        );
      }

      return null;
    },
    [classes.fullDate, classes.timer, date, isLiveTournament, padStart, classes.fullDateContainer]
  );

  if (!isPendingTournament && !isCheckinTournament && !isLiveTournament) {
    return null;
  }

  if (isCheckinTournament && moment(date).isBefore(moment())) {
    return (
      <Typography
        className={classNames(classes.timer, classes.loading, ellipsisAnimationClasses.loading)}
      >
        Tournament Starting
      </Typography>
    );
  }

  return <Countdown date={date} renderer={updateTimeHandler} />;
};
